import React, { useState, useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Container, Row, Col } from "reactstrap";
import {
  Search,
  Calendar,
  Clock,
  ArrowRight,
  Download,
  FileText,
  CheckCircle,
  Share2,
  Bookmark,
  Sparkles,
  PlayCircle,
  BookOpen
} from "react-feather";
import { BANNER_BG, PRIMARY_COLOR, PRIMARY_TEXT_COLOR } from "../../color";
import { BLOG_CATEGORIES, BLOG_POSTS } from "./blogData";
import "./Blogs.css";

const Blogs = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("All Topics");
  const [emailInput, setEmailInput] = useState("");
  const [subscribed, setSubscribed] = useState(false);
  const navigate = useNavigate();

  // Featured Article
  const featuredArticle = useMemo(() => {
    return BLOG_POSTS.find((post) => post.featured) || BLOG_POSTS[0];
  }, []);

  // Popular Articles
  const popularArticles = useMemo(() => {
    return BLOG_POSTS.filter((post) => post.popular).slice(0, 4);
  }, []);

  // Filtered Articles for Main Grid
  const filteredArticles = useMemo(() => {
    return BLOG_POSTS.filter((post) => {
      const matchesSearch =
        post.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        post.excerpt.toLowerCase().includes(searchTerm.toLowerCase()) ||
        post.category.toLowerCase().includes(searchTerm.toLowerCase());

      const matchesCategory =
        selectedCategory === "All Topics" || post.category === selectedCategory;

      return matchesSearch && matchesCategory;
    });
  }, [searchTerm, selectedCategory]);

  const handleSubscribe = (e) => {
    e.preventDefault();
    if (emailInput.trim()) {
      setSubscribed(true);
      setEmailInput("");
      setTimeout(() => setSubscribed(false), 4000);
    }
  };

  return (
    <div className="blogs-wrapper">
      {/* Consistent Hero Banner Section */}
      <div
        className="position-relative overflow-hidden mb-4"
        style={{
          background: BANNER_BG,
          borderBottom: "1px solid rgba(193, 109, 24, 0.15)"
        }}
      >
        <Container className="py-5">
          <Row className="align-items-center gy-4">
            <Col lg={6} className="text-center text-lg-start">
              {/* <div className="d-inline-flex align-items-center gap-2 bg-white rounded-pill px-3 py-2 mb-3 shadow-sm">
                <BookOpen size={18} style={{ color: PRIMARY_COLOR }} />
                <span className="fw-medium" style={{ color: PRIMARY_COLOR, fontSize: "14px", marginLeft: "6px" }}>
                  BLOGS & CAREER RESOURCES
                </span>
              </div> */}

              <h1
                className="display-6 fw-bold mb-3 bannerText"
                style={{ color: PRIMARY_COLOR }}
              >
                Blogs & Career Resources
              </h1>
              <p className="lead mb-4" style={{ color: "black" }}>
                Explore the latest tips, guides, and audit insights to help you learn, grow, and build a successful career.
              </p>

              {/* Search Bar */}
              <div className="blogs-search-container" style={{ margin: "0 0 10px 0", maxWidth: "480px" }}>
                <input
                  type="text"
                  className="blogs-search-input"
                  placeholder="Search for articles, audit guides..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
                <button className="blogs-search-btn" aria-label="Search">
                  <Search size={18} />
                </button>
              </div>
            </Col>
            <Col lg={6} className="d-flex justify-content-center align-items-center">
              <img
                src="/assets/img/rb_9814.png"
                alt="Blogs Hero Banner"
                className="img-fluid"
                style={{ maxHeight: "310px", objectFit: "contain" }}
              />
            </Col>
          </Row>
        </Container>
      </div>

      <div className="container">

        {/* Category Tabs */}
        <div className="blogs-category-tabs">
          {BLOG_CATEGORIES.map((category) => (
            <button
              key={category}
              className={`category-tab-btn ${selectedCategory === category ? "active" : ""
                }`}
              onClick={() => setSelectedCategory(category)}
            >
              {category}
            </button>
          ))}
        </div>

        {/* Featured Hero Article - Compact & Balanced */}
        {selectedCategory === "All Topics" && !searchTerm && featuredArticle && (
          <div className="featured-hero-card">
            <div className="featured-hero-image-col">
              <img src={featuredArticle.image} alt={featuredArticle.title} />
            </div>
            <div className="featured-hero-content-col">
              <span className="featured-badge">Featured Article</span>
              <h2 className="featured-hero-title">{featuredArticle.title}</h2>
              <p className="featured-hero-excerpt">{featuredArticle.excerpt}</p>
              <div className="featured-meta">
                <span>
                  <Clock size={13} style={{ marginRight: 4 }} /> {featuredArticle.readTime}
                </span>
                <span>
                  <Calendar size={13} style={{ marginRight: 4 }} /> {featuredArticle.date}
                </span>
                <span className="featured-cat-tag">{featuredArticle.category}</span>
              </div>
              <Link to={`/blog/${featuredArticle.id}`} className="read-hero-btn">
                Read Full Article <ArrowRight size={15} />
              </Link>
            </div>
          </div>
        )}

        {/* Main Grid: Articles + Sidebar */}
        <div className="blogs-main-grid">
          {/* Left Column: Articles */}
          <div className="articles-column">
            <div className="section-title-wrapper">
              <h2 className="section-title">
                {selectedCategory === "All Topics"
                  ? "Latest Articles"
                  : `${selectedCategory} Articles`}
              </h2>
              <span style={{ fontSize: "0.85rem", color: "#666" }}>
                Showing {filteredArticles.length} results
              </span>
            </div>

            {filteredArticles.length === 0 ? (
              <div
                style={{
                  textAlign: "center",
                  padding: "50px 20px",
                  background: "#ffffff",
                  borderRadius: "12px",
                  border: "1px solid #eee"
                }}
              >
                <h4>No articles found</h4>
                <p style={{ color: "#777" }}>
                  Try adjusting your search terms or selecting a different category.
                </p>
                <button
                  className="promo-btn"
                  style={{ marginTop: 10 }}
                  onClick={() => {
                    setSearchTerm("");
                    setSelectedCategory("All Topics");
                  }}
                >
                  Reset Filters
                </button>
              </div>
            ) : (
              <div className="articles-grid">
                {filteredArticles.map((article) => (
                  <div
                    key={article.id}
                    className="blog-card"
                    onClick={() => navigate(`/blog/${article.id}`)}
                  >
                    <div className="blog-card-img-wrapper">
                      <img src={article.image} alt={article.title} />

                    </div>
                    <div className="blog-card-body">
                      <div className="blog-card-category">{article.category}</div>
                      <h3 className="blog-card-title">{article.title}</h3>
                      <p className="blog-card-excerpt">{article.excerpt}</p>
                      <div className="blog-card-footer">
                        <span>{article.date}</span>
                        <span>{article.readTime}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Right Column: Sidebar */}
          <div className="sidebar-column">
            {/* Popular Articles Widget */}
            <div className="sidebar-widget">
              <h3 className="widget-title">Popular Articles</h3>
              {popularArticles.map((post) => (
                <div
                  key={post.id}
                  className="popular-article-item"
                  onClick={() => navigate(`/blog/${post.id}`)}
                >
                  <img
                    src={post.image}
                    alt={post.title}
                    className="popular-article-img"
                  />
                  <div className="popular-article-info">
                    <span className="popular-article-title">{post.title}</span>
                    <span className="popular-article-time">{post.readTime}</span>
                  </div>
                </div>
              ))}
            </div>

            {/* WhatsApp Community Widget */}
            <div className="sidebar-widget" style={{ textAlign: 'center', padding: '30px 20px' }}>
              <div style={{ background: '#e8f5e9', width: '60px', height: '60px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 15px' }}>
                <img src="https://upload.wikimedia.org/wikipedia/commons/6/6b/WhatsApp.svg" alt="WhatsApp" style={{ width: '35px' }} />
              </div>
              <h3 style={{ fontSize: '1.2rem', fontWeight: 700, color: '#1a1a1a', marginBottom: '10px' }}>Join Our Community</h3>
              <p style={{ fontSize: '0.9rem', color: '#666', marginBottom: '20px' }}>
                Get instant job updates, interview tips, and connect with other field auditors on our exclusive WhatsApp group.
              </p>
              <button
                style={{
                  background: '#25D366',
                  color: '#fff',
                  border: 'none',
                  padding: '10px 20px',
                  borderRadius: '25px',
                  fontWeight: 600,
                  width: '100%',
                  cursor: 'pointer',
                  transition: 'background 0.2s'
                }}
                onMouseOver={(e) => e.target.style.background = '#20b858'}
                onMouseOut={(e) => e.target.style.background = '#25D366'}
                onClick={() => window.open('https://chat.whatsapp.com/your-invite-link', '_blank')}
              >
                Join WhatsApp Group
              </button>
            </div>
          </div>
        </div>

        {/* CTA Banner */}
        <div className="promo-banner">
          <div className="promo-text">
            <h3>Create Your Professional Resume with BetelJobs</h3>
            <p>Build a job-winning resume in minutes and get noticed by top employers and audit firms across India.</p>
          </div>
          <button
            className="promo-btn"
            onClick={() => navigate("/")}
          >
            Create Resume Now →
          </button>
        </div>
      </div>
    </div>
  );
};

export default Blogs;
